"use client";

import { Button } from "@/design-system";
import type { FileUploadOptions, SurveyQuestion } from "@/types/survey";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

interface FileUploadQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  answer?: string | number | boolean | string[];
  isPreview?: boolean;
  selectedValue?: string | number | string[];
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  /**
   * Survey id used to scope file uploads to this survey's organization.
   * Required for real uploads — when omitted, the component falls back
   * to the legacy filename-only payload so the editor preview keeps
   * working without making any network calls.
   */
  surveyId?: string;
  uiStrings?: {
    allFileTypesAllowed: string;
    allowedTypes: string;
    maxSize: string;
    upToFiles: (count: number) => string;
    dragFiles: string;
    chooseFiles: string;
    uploading: string;
    uploadFailed: string;
    retry: string;
    remove: string;
    fileLimit: (count: number) => string;
    fileTooLarge: (name: string, sizeMb: number) => string;
    fileTypeNotAllowed: (name: string) => string;
  };
}

function parseFileUploadOptions(
  raw: Record<string, unknown> | null | undefined,
): FileUploadOptions {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const allowed = raw.allowedFileTypes;
  const allowedFileTypes = Array.isArray(allowed)
    ? allowed.filter((t): t is string => typeof t === "string")
    : undefined;
  const maxFileSizeMB =
    typeof raw.maxFileSizeMB === "number" && Number.isFinite(raw.maxFileSizeMB)
      ? raw.maxFileSizeMB
      : undefined;
  const maxFiles =
    typeof raw.maxFiles === "number" && Number.isFinite(raw.maxFiles)
      ? raw.maxFiles
      : undefined;
  const instructions =
    typeof raw.instructions === "string" ? raw.instructions : undefined;
  return { allowedFileTypes, maxFileSizeMB, maxFiles, instructions };
}

function expandAllowedPatterns(types: string[]): string[] {
  const out: string[] = [];
  for (const t of types) {
    const trimmed = t.trim();
    if (!trimmed) continue;
    trimmed.split(",").forEach((part) => {
      const p = part.trim();
      if (p) out.push(p);
    });
  }
  return out;
}

function fileMatchesAllowedType(file: File, patterns: string[]): boolean {
  if (patterns.length === 0) return true;
  const mime = file.type || "";
  for (const pattern of patterns) {
    if (pattern.startsWith(".")) {
      const ext = pattern.toLowerCase();
      if (file.name.toLowerCase().endsWith(ext)) return true;
      continue;
    }
    if (pattern === "*/*") return true;
    if (pattern.endsWith("/*")) {
      const prefix = pattern.slice(0, -1);
      if (mime.startsWith(prefix)) return true;
      continue;
    }
    if (mime === pattern) return true;
  }
  return false;
}

/**
 * One row in the visible "files" list. We track the upload lifecycle
 * here so respondents can see what's happening with each pick.
 *
 * `serverFileId` only gets populated once the file successfully lands
 * in S3 — the answer payload is derived from entries where this is
 * set, so half-uploaded files are never submitted as answers.
 */
type UploadEntry = {
  /** Local-only id used as the React key. Stable across re-renders. */
  localId: string;
  /** The original picked File. Null when restored from a saved answer. */
  file: File | null;
  /** Filename to show in the UI. */
  name: string;
  /** Size in bytes when known (picked files only). */
  size: number;
  /** Lifecycle status. */
  status: "uploading" | "uploaded" | "error";
  /** Server File id once the upload completes. */
  serverFileId?: string;
  /** Error message when status === "error". */
  errorMessage?: string;
};

/**
 * Parse the saved answer payload into UploadEntry shapes for display.
 * Supports two formats:
 *
 *   1. New: `JSON.stringify([{ id, name }, ...])` — entries written by
 *      this component after a successful upload.
 *   2. Legacy: `JSON.stringify([name, ...])` or `[name, ...]` — older
 *      responses written before the upload flow was wired up. We can
 *      still display the filenames; the entries just have no
 *      serverFileId so they render without a Download affordance.
 */
function parseAnswerToEntries(
  answer: FileUploadQuestionProps["answer"],
): UploadEntry[] {
  if (answer === undefined || answer === null) return [];
  let raw: unknown = answer;
  if (typeof raw === "string") {
    if (!raw.trim()) return [];
    try {
      raw = JSON.parse(raw);
    } catch {
      // Treat a non-JSON string as a single legacy filename.
      return [
        {
          localId: makeLocalId(),
          file: null,
          name: answer as string,
          size: 0,
          status: "uploaded",
        },
      ];
    }
  }
  if (!Array.isArray(raw)) return [];
  const out: UploadEntry[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      out.push({
        localId: makeLocalId(),
        file: null,
        name: item,
        size: 0,
        status: "uploaded",
      });
    } else if (item && typeof item === "object") {
      const obj = item as { id?: unknown; name?: unknown };
      const name = typeof obj.name === "string" ? obj.name : "";
      const id = typeof obj.id === "string" ? obj.id : undefined;
      if (!name) continue;
      out.push({
        localId: makeLocalId(),
        file: null,
        name,
        size: 0,
        status: "uploaded",
        serverFileId: id,
      });
    }
  }
  return out;
}

function entriesToAnswerPayload(entries: UploadEntry[]): string {
  // Only include entries that have a server file id — respondents may
  // have started another upload that hasn't finished yet, and we don't
  // want to submit half-uploaded files as answers.
  const completed = entries
    .filter((e) => e.status === "uploaded")
    .map((e) =>
      e.serverFileId
        ? { id: e.serverFileId, name: e.name }
        : // Legacy entries without an id round-trip as plain strings so
          // existing answers don't lose data when re-saved.
          e.name,
    );
  return JSON.stringify(completed);
}

function makeLocalId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getUploadErrorFromBody(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  if (typeof record.error === "string" && record.error.trim()) {
    return record.error.trim();
  }
  if (typeof record.message === "string" && record.message.trim()) {
    return record.message.trim();
  }
  if (Array.isArray(record.errors)) {
    const firstError = record.errors.find(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof (item as Record<string, unknown>).error === "string",
    ) as Record<string, unknown> | undefined;
    const message = firstError?.error;
    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }
  return null;
}

export async function getFileUploadResponseErrorMessage(
  response: Response,
  fallback: string,
) {
  try {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const message = getUploadErrorFromBody(await response.json());
      if (message) return message;
    } else {
      const text = (await response.text()).trim();
      if (text) return text;
    }
  } catch {
    // Fall back to the caller's status-based message when the response body
    // cannot be parsed or has already been consumed.
  }
  return fallback;
}

/**
 * Two-step upload that mirrors the authenticated `LogoFilePicker`
 * flow but goes through public survey endpoints scoped by surveyId:
 *
 *   1. POST /api/survey-public/{surveyId}/upload-file
 *      Creates a private File record + returns its server id.
 *   2. PUT  /api/survey-public/{surveyId}/upload-file/{fileId}
 *      Streams the bytes; the API writes them to S3 and flips
 *      `isUploaded`. Sent as `application/octet-stream` so the
 *      route handler reads the raw buffer.
 */
async function uploadFileToServer(
  surveyId: string,
  questionId: string,
  file: File,
): Promise<{ id: string; name: string }> {
  const createRes = await fetch(
    `/api/survey-public/${surveyId}/upload-file`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        questionId,
        name: file.name,
        mime: file.type || "application/octet-stream",
      }),
    },
  );
  if (!createRes.ok) {
    throw new Error(
      await getFileUploadResponseErrorMessage(
        createRes,
        `Failed to prepare upload (${createRes.status})`,
      ),
    );
  }
  const data = (await createRes.json()) as {
    file?: { id?: string; name?: string };
  };
  const fileId = data.file?.id;
  if (!fileId) throw new Error("Upload response missing file id");

  const putRes = await fetch(
    `/api/survey-public/${surveyId}/upload-file/${fileId}`,
    {
      method: "PUT",
      // Send as octet-stream so the API route reads a Buffer. The
      // server detects the real mime via fileTypeFromBuffer.
      headers: { "Content-Type": "application/octet-stream" },
      credentials: "omit",
      body: file,
    },
  );
  if (!putRes.ok) {
    throw new Error(
      await getFileUploadResponseErrorMessage(
        putRes,
        `Failed to upload file bytes (${putRes.status})`,
      ),
    );
  }

  return { id: fileId, name: data.file?.name || file.name };
}

export default function FileUploadQuestion({
  question,
  brandColor = "var(--color-alias-info)",
  onAnswerChange,
  answer,
  isPreview = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedValue,
  onPreviewAnswerChange,
  surveyId,
  uiStrings,
}: FileUploadQuestionProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const entriesRef = useRef<UploadEntry[]>([]);
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks the JSON payload we most recently pushed up via
  // `pushAnswer`. The hydration effect uses this to tell apart "the
  // parent re-rendered because of our own update" (skip) from "an
  // external party set a new answer" (rebuild local state). Without
  // it, picking a file pushes an empty `[]` (no completed uploads
  // yet), the parent re-renders us with that same empty answer, and
  // the hydration effect would happily wipe the in-progress
  // "uploading" entry.
  const lastPushedPayloadRef = useRef<string | null>(null);

  const opts = useMemo(
    () => parseFileUploadOptions(question.options as Record<string, unknown> | null),
    [question.options],
  );

  const allowedPatterns = useMemo(
    () => expandAllowedPatterns(opts.allowedFileTypes ?? []),
    [opts.allowedFileTypes],
  );

  const hasTypeRestriction = allowedPatterns.length > 0;
  const maxFileSizeMB = opts.maxFileSizeMB ?? 10;
  const maxFiles = opts.maxFiles ?? 5;
  const maxBytes = maxFileSizeMB * 1024 * 1024;

  const acceptAttr = useMemo(() => {
    if (!hasTypeRestriction) return undefined;
    return allowedPatterns.filter((p) => !p.startsWith(".")).join(",") || undefined;
  }, [hasTypeRestriction, allowedPatterns]);

  const constraintsSummary = useMemo(() => {
    const parts: string[] = [];
    if (hasTypeRestriction) {
      parts.push(`${uiStrings?.allowedTypes ?? "Allowed types"}: ${allowedPatterns.join(", ")}`);
    } else {
      parts.push(uiStrings?.allFileTypesAllowed ?? "All file types allowed");
    }
    parts.push(`${uiStrings?.maxSize ?? "Max size"}: ${maxFileSizeMB} MB per file`);
    parts.push(uiStrings?.upToFiles?.(maxFiles) ?? `Up to ${maxFiles} file${maxFiles === 1 ? "" : "s"}`);
    return parts;
  }, [allowedPatterns, hasTypeRestriction, maxFileSizeMB, maxFiles, uiStrings]);

  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  const pushAnswer = useCallback(
    (next: UploadEntry[]) => {
      const payload = entriesToAnswerPayload(next);
      // Remember what we pushed so the hydration effect can recognize
      // its own echo and skip overwriting in-progress uploads.
      lastPushedPayloadRef.current = payload;
      if (isPreview && onPreviewAnswerChange) {
        onPreviewAnswerChange(question.id, payload);
      } else if (onAnswerChange) {
        onAnswerChange(question, payload);
      }
    },
    [isPreview, onAnswerChange, onPreviewAnswerChange, question],
  );

  // Helper to mutate one entry by localId without losing concurrent
  // updates from other in-flight uploads. Always operates against the
  // freshest list via the ref.
  const updateEntry = useCallback(
    (localId: string, patch: Partial<UploadEntry>) => {
      const next = entriesRef.current.map((e) =>
        e.localId === localId ? { ...e, ...patch } : e,
      );
      entriesRef.current = next;
      setEntries(next);
      pushAnswer(next);
    },
    [pushAnswer],
  );

  // Hydrate from parent answer (e.g. resume from a saved draft). Only
  // runs when the answer changed from an *external* source — if the
  // incoming answer matches the JSON payload we just pushed up
  // ourselves, we skip entirely. That's important: while a file is
  // uploading we publish an empty completed-files array, and without
  // this guard the parent re-render that comes back would clobber the
  // in-progress entry from local state.
  useEffect(() => {
    if (isPreview) return;
    // Normalize the incoming answer into the same JSON-string form
    // that pushAnswer produces so the comparison is symmetric.
    const incoming =
      typeof answer === "string"
        ? answer
        : answer === undefined || answer === null
          ? null
          : JSON.stringify(answer);
    if (incoming !== null && incoming === lastPushedPayloadRef.current) {
      return;
    }
    const parsed = parseAnswerToEntries(answer);
    setEntries((prev) => {
      // Public survey autosaves can echo an older answer payload while
      // multiple uploads are still resolving. Do not let a stale smaller
      // payload remove optimistic upload rows; explicit user removals still
      // flow through because removeAt updates lastPushedPayloadRef first.
      if (incoming !== null && parsed.length < prev.length) {
        entriesRef.current = prev;
        return prev;
      }
      const sameLength = parsed.length === prev.length;
      const sameContents =
        sameLength &&
        parsed.every((p, i) => {
          const q = prev[i];
          return (
            q &&
            q.name === p.name &&
            (q.serverFileId ?? null) === (p.serverFileId ?? null)
          );
        });
      if (sameContents) return prev;
      entriesRef.current = parsed;
      return parsed;
    });
  }, [answer, isPreview]);

  const startUpload = useCallback(
    async (entry: UploadEntry, file: File) => {
      if (!surveyId) {
        // Without a surveyId we can't upload; fall back to legacy
        // name-only behavior so the editor preview keeps working.
        updateEntry(entry.localId, {
          status: "uploaded",
          errorMessage: undefined,
        });
        return;
      }
      try {
        const { id, name } = await uploadFileToServer(surveyId, question.id, file);
        updateEntry(entry.localId, {
          status: "uploaded",
          serverFileId: id,
          name,
          errorMessage: undefined,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to upload file";
        updateEntry(entry.localId, {
          status: "error",
          errorMessage: message,
        });
      }
    },
    [question.id, surveyId, updateEntry],
  );

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (isPreview) return;
      const next = [...entriesRef.current];
      const newlyAccepted: Array<{ entry: UploadEntry; file: File }> = [];
      let lastError: string | null = null;
      for (const file of incoming) {
        if (next.length >= maxFiles) {
          lastError = uiStrings?.fileLimit?.(maxFiles) ?? `You can upload at most ${maxFiles} file${maxFiles === 1 ? "" : "s"}.`;
          break;
        }
        if (file.size > maxBytes) {
          lastError = uiStrings?.fileTooLarge?.(file.name, maxFileSizeMB) ?? `"${file.name}" exceeds the maximum size of ${maxFileSizeMB} MB.`;
          continue;
        }
        if (!fileMatchesAllowedType(file, allowedPatterns)) {
          lastError = uiStrings?.fileTypeNotAllowed?.(file.name) ?? `"${file.name}" is not an allowed file type.`;
          continue;
        }
        const entry: UploadEntry = {
          localId: makeLocalId(),
          file,
          name: file.name,
          size: file.size,
          status: "uploading",
        };
        next.push(entry);
        newlyAccepted.push({ entry, file });
      }
      setError(lastError);
      entriesRef.current = next;
      setEntries(next);
      pushAnswer(next);
      if (inputRef.current) inputRef.current.value = "";
      // Kick off uploads after committing the optimistic UI state so
      // the rows render in their "uploading" state immediately.
      for (const item of newlyAccepted) {
        void startUpload(item.entry, item.file);
      }
    },
    [
      allowedPatterns,
      isPreview,
      maxBytes,
      maxFileSizeMB,
      maxFiles,
      pushAnswer,
      startUpload,
      uiStrings,
    ],
  );

  const removeAt = useCallback(
    (localId: string) => {
      if (isPreview) return;
      setError(null);
      const next = entriesRef.current.filter((e) => e.localId !== localId);
      entriesRef.current = next;
      setEntries(next);
      pushAnswer(next);
    },
    [isPreview, pushAnswer],
  );

  const retry = useCallback(
    (entry: UploadEntry) => {
      if (!entry.file) return;
      updateEntry(entry.localId, {
        status: "uploading",
        errorMessage: undefined,
      });
      void startUpload(entry, entry.file);
    },
    [startUpload, updateEntry],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    addFiles(Array.from(list));
  };

  const onDragOver = (e: React.DragEvent) => {
    if (isPreview || entries.length >= maxFiles) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent) => {
    if (isPreview || entries.length >= maxFiles) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dt = e.dataTransfer.files;
    if (dt?.length) addFiles(Array.from(dt));
  };

  const atMax = entries.length >= maxFiles;
  const dropZoneBorder = isDragging
    ? brandColor
    : "var(--color-alias-neutral-light)";
  const dropZoneBackground = "var(--color-alias-white)";

  if (isPreview) {
    return (
      <div className="mb-4 w-full">
        <label
          className="block text-sm font-medium mb-2 text-left"
          style={{ color: "var(--sl-text, var(--color-alias-neutral-darkest))" }}
        >
          {question.text}
        </label>
        <p
          className="text-sm mb-2 text-left"
          style={{ color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" }}
        >
          {constraintsSummary.join(" · ")}
        </p>
        <div
          className="rounded-lg border-2 border-dashed p-6 flex flex-col items-center justify-center gap-3 text-center pointer-events-none select-none"
          style={{
            borderColor: "var(--color-alias-neutral-light)",
            backgroundColor: dropZoneBackground,
          }}
        >
          <span
            className="text-sm"
            style={{ color: "var(--color-alias-neutral-dark)" }}
          >
            {uiStrings?.dragFiles ?? "Drag files here or choose from your device"}
          </span>
          <Button type="button" variant="secondary" size="sm" disabled>
            {uiStrings?.chooseFiles ?? "Choose files"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 w-full">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium mb-2 text-left"
        style={{ color: "var(--sl-text, var(--color-alias-neutral-darkest))" }}
      >
        {question.text}
      </label>
      <p
        className="text-sm mb-2 text-left"
        style={{ color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" }}
      >
        {constraintsSummary.join(" · ")}
      </p>

      <div
        className="rounded-lg border-2 border-dashed p-6 flex flex-col items-center justify-center gap-3 text-center transition-colors"
        style={{
          borderColor: dropZoneBorder,
          backgroundColor: dropZoneBackground,
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="sr-only"
          style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}
          accept={acceptAttr}
          multiple={maxFiles > 1}
          disabled={atMax}
          onChange={onInputChange}
          aria-disabled={atMax}
        />
        <span
          className="text-sm"
          style={{ color: "var(--color-alias-neutral-dark)" }}
        >
          {uiStrings?.dragFiles ?? "Drag files here or choose from your device"}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={atMax}
          onClick={() => inputRef.current?.click()}
        >
          {uiStrings?.chooseFiles ?? "Choose files"}
        </Button>
      </div>

      {error ? (
        <p className="text-sm mt-2" role="alert" style={{ color: "var(--color-alias-error)" }}>
          {error}
        </p>
      ) : null}

      {entries.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2 text-left list-none p-0 m-0">
          {entries.map((entry) => (
            <li
              key={entry.localId}
              className="flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm"
              style={{
                backgroundColor: "var(--color-alias-white)",
                outline: "1px solid var(--color-alias-neutral-light)",
              }}
            >
              <span
                className="flex-1 min-w-0"
                style={{ color: "var(--color-alias-neutral-darkest)" }}
              >
                <span className="break-all">{entry.name}</span>
                {entry.size > 0 ? (
                  <span style={{ color: "var(--color-alias-neutral-dark)" }}>
                    {" "}
                    ({Math.round(entry.size / 1024)} KB)
                  </span>
                ) : null}
                {entry.status === "uploading" ? (
                  <span
                    className="ml-2 text-xs"
                    style={{ color: "var(--color-alias-neutral-dark)" }}
                  >
                    {uiStrings?.uploading ?? "Uploading…"}
                  </span>
                ) : null}
                {entry.status === "error" ? (
                  <span
                    className="ml-2 text-xs"
                    style={{ color: "var(--color-alias-error)" }}
                  >
                    {entry.errorMessage || uiStrings?.uploadFailed || "Upload failed"}
                  </span>
                ) : null}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {entry.status === "error" && entry.file ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => retry(entry)}
                  >
                    {uiStrings?.retry ?? "Retry"}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => removeAt(entry.localId)}
                  style={{ color: "var(--color-alias-info)" }}
                >
                  {uiStrings?.remove ?? "Remove"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
